import React, { useState } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { MultiSelect } from "../components/duvi/MultiSelect";
import { Input } from "../components/duvi/Input";
import { Search, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";
import { tickets as ticketsApi } from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

export function SupportTicketsList() {
  const navigate = useNavigate();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: ticketsList,
    isLoading,
    meta,
  } = useQuery(
    () =>
      ticketsApi.list({
        status: selectedStatuses.filter(Boolean).join(",") || undefined,
        priority: selectedPriorities.filter(Boolean).join(",") || undefined,
        category: selectedCategories.filter(Boolean).join(",") || undefined,
        search: searchQuery || undefined,
        limit: 5,
      }),
    [
      selectedStatuses,
      selectedPriorities,
      selectedCategories,
      searchQuery,
      currentPage,
    ],
  );

  const tickets = ticketsList ?? [];
  const totalCount = meta?.total_count ?? tickets.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / 5));
  const slaWarningCount = 0;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "#EF4444";
      case "high":
        return "#F59E0B";
      case "medium":
        return "#3B82F6";
      case "low":
        return "#64748B";
      default:
        return "#64748B";
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "open":
        return "default";
      case "in-progress":
        return "warning";
      case "awaiting":
        return "default";
      case "resolved":
        return "success";
      case "closed":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "in-progress":
        return "In Progress";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <AppShell
      breadcrumbs={["Support", "Tickets"]}
      activeMenuItem="Support"
      notificationCount={3}
    >
      <div data-testid={testIds.ticketsPage} className="space-y-6">
        {/* Header */}
        <div>
          <h1
            className="text-duvi-text-primary"
            style={{
              fontSize: "var(--text-xl)",
              fontWeight: "var(--font-weight-semibold)",
              lineHeight: "var(--line-height-heading)",
            }}
          >
            Support Tickets
          </h1>
        </div>

        {/* SLA Warning Banner */}
        {slaWarningCount > 0 && (
          <div className="bg-duvi-warning/10 border border-duvi-warning/20 rounded-[8px] p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-duvi-warning flex-shrink-0" />
              <div className="flex-1">
                <span
                  className="text-duvi-text-primary"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {slaWarningCount} ticket
                  {slaWarningCount > 1 ? "s are" : " is"} approaching SLA —
                </span>
                <button
                  className="text-duvi-warning hover:underline ml-1"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  view them
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters Row */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status */}
          <div
            className="flex-1 min-w-[180px]"
            data-testid={testIds.ticketFilterStatus}
          >
            <MultiSelect
              options={[
                { value: "open", label: "Open" },
                { value: "in-progress", label: "In Progress" },
                { value: "awaiting", label: "Awaiting" },
                { value: "resolved", label: "Resolved" },
                { value: "closed", label: "Closed" },
              ]}
              value={selectedStatuses.filter(Boolean)}
              onChange={setSelectedStatuses}
              placeholder="All Statuses"
              searchPlaceholder="Search statuses..."
            />
          </div>

          {/* Priority */}
          <div
            className="flex-1 min-w-[180px]"
            data-testid={testIds.ticketFilterPriority}
          >
            <MultiSelect
              options={[
                { value: "urgent", label: "Urgent" },
                { value: "high", label: "High" },
                { value: "medium", label: "Medium" },
                { value: "low", label: "Low" },
              ]}
              value={selectedPriorities.filter(Boolean)}
              onChange={setSelectedPriorities}
              placeholder="All Priorities"
              searchPlaceholder="Search priorities..."
            />
          </div>

          {/* Category */}
          <div className="flex-1 min-w-[180px]">
            <MultiSelect
              options={[
                { value: "technical", label: "Technical" },
                { value: "billing", label: "Billing" },
                { value: "feature", label: "Feature Request" },
                { value: "question", label: "Question" },
              ]}
              value={selectedCategories.filter(Boolean)}
              onChange={setSelectedCategories}
              placeholder="All Categories"
              searchPlaceholder="Search categories..."
            />
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[250px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-duvi-text-secondary" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tickets..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Tickets Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-duvi-border">
                <tr>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Priority
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Ticket ID
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Subject
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Submitter
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Status
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Age
                  </th>
                  <th
                    className="text-left py-3 px-4 text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                    }}
                  >
                    Last Reply
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-8 text-center text-duvi-text-secondary"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      Loading tickets...
                    </td>
                  </tr>
                ) : tickets.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-8 text-center text-duvi-text-secondary"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      <span data-testid={testIds.ticketEmpty}>
                        No tickets found
                      </span>
                    </td>
                  </tr>
                ) : (
                  tickets.map((ticket) => (
                    <tr
                      key={ticket.ticket_id}
                      data-testid={testIds.ticketRow}
                      onClick={() =>
                        navigate(`/support/tickets/${ticket.ticket_id}`)
                      }
                      className="border-b border-duvi-border hover:bg-duvi-elevated cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="relative group">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: getPriorityColor(
                                ticket.priority,
                              ),
                            }}
                          />
                          {/* Tooltip */}
                          <div
                            className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 px-2 py-1 bg-duvi-elevated border border-duvi-border rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10"
                            style={{ fontSize: "var(--text-2xs)" }}
                          >
                            {ticket.priority.charAt(0).toUpperCase() +
                              ticket.priority.slice(1)}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <a
                          href={`/support/tickets/${ticket.ticket_id}`}
                          onClick={(e) => e.preventDefault()}
                          className="text-duvi-accent hover:underline font-mono"
                          style={{
                            fontSize: "var(--text-xs)",
                            fontFamily: "monospace",
                          }}
                        >
                          {ticket.ticket_id}
                        </a>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="text-duvi-text-primary truncate block max-w-md"
                          style={{ fontSize: "var(--text-sm)" }}
                        >
                          {ticket.subject}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-duvi-text-primary"
                            style={{ fontSize: "var(--text-sm)" }}
                          >
                            {ticket.submitter_id}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {ticket.submitter_role}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={getStatusBadgeVariant(ticket.status)}>
                          {getStatusLabel(ticket.status)}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="text-duvi-text-secondary"
                          style={{ fontSize: "var(--text-xs)" }}
                        >
                          {ticket.created_at}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="text-duvi-text-secondary"
                          style={{ fontSize: "var(--text-xs)" }}
                        >
                          {ticket.updated_at}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-duvi-border">
            <div
              data-testid={testIds.ticketPaginationInfo}
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              Showing {Math.min((currentPage - 1) * 5 + 1, totalCount)}-
              {Math.min(currentPage * 5, totalCount)} of {totalCount} tickets
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid={testIds.ticketPaginationPrev}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1.5 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary hover:bg-duvi-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontSize: "var(--text-sm)" }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span
                className="text-duvi-text-primary"
                style={{ fontSize: "var(--text-sm)" }}
              >
                Page {currentPage} of {totalPages}
              </span>
              <button
                data-testid={testIds.ticketPaginationNext}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1.5 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary hover:bg-duvi-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontSize: "var(--text-sm)" }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
